import React, { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Warning } from '@phosphor-icons/react';
import {
  PAYER_PROGRAMS,
  PROCEDURE_CODES,
  PAYER_TYPES,
} from '../../constants/patientConstants';

/**
 * Inline per-claim service-line picker used inside the bulk
 * "Submit to ODM" dialog (and individual submit confirm dialog).
 *
 * Props:
 *   claim:    { claim_id, patient_billing_lines, claim_payer_type,
 *               claim_payer_program, service_code, ... }
 *   override: { payer_type?, payer_program?, service_code? } or undefined
 *   onOverride: (claim_id, {payer_type, payer_program, service_code}) => void
 *
 * Visual behavior:
 *   - Auto-selects the saved line that matches the claim's current values.
 *   - If the claim's values don't match any saved line, shows an amber
 *     "doesn't match patient's saved lines" warning (user can still
 *     submit without changing — Approach 2a).
 *   - Saved patient lines are listed first, with a "Custom — keep current"
 *     option at the bottom for the off-policy case.
 */
export const ClaimLinePicker = ({ claim, override, onOverride }) => {
  // Memoize the lines array so the dependent useMemo hooks below don't see
  // a new reference on every render (which would defeat their caching and
  // tripped `react-hooks/exhaustive-deps` before this wrapper).
  const lines = useMemo(
    () => (Array.isArray(claim?.patient_billing_lines) ? claim.patient_billing_lines : []),
    [claim?.patient_billing_lines],
  );

  // Resolve "current values" — override takes priority over the claim's
  // own fields so the dropdown stays consistent across re-renders.
  const current = useMemo(() => ({
    payer_type: override?.payer_type ?? claim?.claim_payer_type ?? '',
    payer_program: override?.payer_program ?? claim?.claim_payer_program ?? '',
    service_code: override?.service_code ?? claim?.service_code ?? '',
  }), [override, claim]);

  const matchingIdx = useMemo(() => {
    return lines.findIndex((ln) =>
      (ln.payer_type || '') === current.payer_type &&
      (ln.payer_program || '') === current.payer_program &&
      (ln.procedure_code || '') === current.service_code
    );
  }, [lines, current]);

  const labelFor = (ln) => {
    const pt = PAYER_TYPES.find((p) => p.value === ln.payer_type)?.label || ln.payer_type || '—';
    const pp = PAYER_PROGRAMS.find((p) => p.value === ln.payer_program)?.label || ln.payer_program || '—';
    const pc = PROCEDURE_CODES.find((p) => p.value === ln.procedure_code)?.label || ln.procedure_code || '—';
    return `${pt}  ·  ${pp}  ·  ${pc}`;
  };

  const currentLabel = () => {
    const fake = {
      payer_type: current.payer_type,
      payer_program: current.payer_program,
      procedure_code: current.service_code,
    };
    return labelFor(fake);
  };

  const handlePick = (val) => {
    if (val === '__keep__') {
      onOverride?.(claim.claim_id, null); // clear override → use claim's own values
      return;
    }
    const idx = parseInt(val, 10);
    const ln = lines[idx];
    if (!ln) return;
    onOverride?.(claim.claim_id, {
      payer_type: ln.payer_type || '',
      payer_program: ln.payer_program || '',
      service_code: ln.procedure_code || '',
    });
  };

  const selectedValue = matchingIdx >= 0 ? String(matchingIdx) : '__keep__';
  const mismatch = matchingIdx === -1 && lines.length > 0;

  return (
    <div className="mt-2 space-y-1" data-testid={`claim-line-picker-${claim.claim_id}`}>
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap">
          Service line
        </span>
        <Select value={selectedValue} onValueChange={handlePick}>
          <SelectTrigger
            className="h-7 text-xs flex-1"
            data-testid={`claim-line-picker-trigger-${claim.claim_id}`}
          >
            <SelectValue placeholder={currentLabel()} />
          </SelectTrigger>
          <SelectContent>
            {lines.map((ln, idx) => (
              <SelectItem
                key={`${ln.payer_type || ''}:${ln.payer_program || ''}:${ln.procedure_code || ''}:${idx}`}
                value={String(idx)}
                data-testid={`claim-line-option-${claim.claim_id}-${idx}`}
              >
                <span className="inline-flex items-center gap-2 text-xs">
                  {idx === 0 ? (
                    <span className="text-[9px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">
                      Primary
                    </span>
                  ) : null}
                  <span>{labelFor(ln)}</span>
                </span>
              </SelectItem>
            ))}
            <SelectItem value="__keep__" data-testid={`claim-line-option-keep-${claim.claim_id}`}>
              <span className="text-slate-500 italic text-xs">
                {lines.length === 0 ? 'Keep current values' : 'Custom — keep current values'}
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      {mismatch ? (
        <div
          className="flex items-start gap-1.5 text-[11px] text-amber-800"
          data-testid={`claim-line-mismatch-${claim.claim_id}`}
        >
          <Warning size={12} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
          <span>
            <strong className="font-mono">{currentLabel()}</strong>
            {' '}doesn&rsquo;t match any of this patient&rsquo;s saved lines. You can submit as-is
            or pick a saved line above.
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default ClaimLinePicker;
